/**
 * Chat attachment marker translations (#476).
 *
 * `selectOpenLineMessages` puts `[attachment: image]` / `[attachment: image ×3]`
 * into the transcript so a message that carried only a file does not vanish
 * (#468). That token has two readers with opposite needs:
 *
 * - the LLM and `evaluateChatSignificance` need a STABLE machine form that does
 *   not depend on the project's locale — the token stays as it is for them;
 * - the customer reads the same string in the transcript panel and in the CRM
 *   delivery, where `[attachment: image]` is untranslated plumbing.
 *
 * These atoms are the human half. The word for the file kind comes from the
 * vendor (`files[id].type`), so an unknown kind falls back to the generic
 * "file" — knowing that an attachment WAS there matters more than knowing
 * which kind it was, and that is the same fallback the marker itself uses.
 */

import type { Translations } from './types';

/**
 * Kinds Bitrix24 reports in the open-line history `files` dictionary. Anything
 * else resolves to `attachmentFile`.
 */
export const CHAT_ATTACHMENT_KINDS = [
  'image',
  'video',
  'audio',
  'document',
  'file',
] as const;

export type ChatAttachmentKind = (typeof CHAT_ATTACHMENT_KINDS)[number];

/** Translation key for a vendor-reported kind; unknown kinds get the generic one. */
export function chatAttachmentKindKey(kind: string): string {
  const normalized = kind.trim().toLowerCase();
  return (CHAT_ATTACHMENT_KINDS as readonly string[]).includes(normalized)
    ? `attachment_${normalized}`
    : 'attachment_file';
}

export const chatAttachmentTranslations: Translations = {
  uk: {
    attachment_image: 'вкладення: фото',
    attachment_video: 'вкладення: відео',
    attachment_audio: 'вкладення: аудіо',
    attachment_document: 'вкладення: документ',
    attachment_file: 'вкладення: файл',
    // Label of a quoted message inside a chat reply (#889).
    quote: 'Цитата',
  },

  ru: {
    attachment_image: 'вложение: фото',
    attachment_video: 'вложение: видео',
    attachment_audio: 'вложение: аудио',
    attachment_document: 'вложение: документ',
    attachment_file: 'вложение: файл',
    // Label of a quoted message inside a chat reply (#889).
    quote: 'Цитата',
  },

  en: {
    attachment_image: 'attachment: photo',
    attachment_video: 'attachment: video',
    attachment_audio: 'attachment: audio',
    attachment_document: 'attachment: document',
    attachment_file: 'attachment: file',
    // Label of a quoted message inside a chat reply (#889).
    quote: 'Quote',
  },

  es: {
    attachment_image: 'adjunto: foto',
    attachment_video: 'adjunto: video',
    attachment_audio: 'adjunto: audio',
    attachment_document: 'adjunto: documento',
    attachment_file: 'adjunto: archivo',
    // Label of a quoted message inside a chat reply (#889).
    quote: 'Cita',
  },
};
